import { ComponentProps } from 'react'

import { scrollbarStyleDefaultHidden } from '@/shared/styles/scrollbarStyle'
import { Link, styled, useMediaQuery, useTheme } from '@mui/material'

export type NavSidebarProps = ComponentProps<'nav'> & {
  children: React.ReactNode
  open: boolean
  logoHref?: string
}

export const NavSidebar: React.FC<NavSidebarProps> = ({ children, open, logoHref, ...props }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const logoSrc = isMobile || open ? '/aside-logo.svg' : '/aside-logo-icon.png'

  return (
    <StyledSidebarNav {...props} $open={open}>
      <StyledLogoContainer href={logoHref}>
        <StyledLogo src={logoSrc} />
      </StyledLogoContainer>
      {children}
    </StyledSidebarNav>
  )
}

export const StyledLogoContainer = styled(Link)`
  margin: 0;
  display: flex;
  position: relative;
`

export const StyledLogo = styled('img')`
  height: ${(props) => props.theme.typography.pxToRem(36)};
  margin: ${(props) => props.theme.typography.pxToRem(16)} auto;
`

export const StyledSidebarNav = styled('nav', {
  shouldForwardProp: (prop) => prop !== '$open',
}) <{ $open?: boolean }>`
  position: fixed;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: ${(props) => props.theme.zIndex.appBar + 1};
  height: 100vh;
  background-color: ${(props) => props.theme.palette.custom.sidebar.backgroundColor};
  overflow-y: auto;
  width: ${(props) => props.theme.layout.navSidebarWidth};
  transition: transform ${(props) => props.theme.layout.navSidebarTransitionSpeed}ms ease-in-out;
  transform: translateX(-${(props) => props.theme.layout.navSidebarWidth});
  ${scrollbarStyleDefaultHidden}
  
  ${(props) => props.$open && `
    transform: translateX(0);
  `}

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${(props) => props.theme.layout.navSidebarCollapsedWidth};
    transition: width ${(props) => props.theme.layout.navSidebarTransitionSpeed}ms ease-in-out;
    transform: none;
  
    ${(props) => props.$open && `
      width: ${props.theme.layout.navSidebarWidth};
    `}
  }
`
